import { useEffect, useMemo, useState } from 'react';
import { supabaseClient } from '../../utils/supabase';

/**
 * Editable funder routing / restrictions / contact card shown at the bottom
 * of the funder detail page. This (plus the same fields on the funder record)
 * replaces the Lender Contact Info sheet as the source of truth for how
 * submissions are emailed.
 */

export const ROUTING_MODES = ['Submission Email', 'Both', 'Rep Email', 'Multi Emails', 'Portal'] as const;

export const MODE_HELP: Record<string, string> = {
  'Submission Email': 'To: submission email',
  'Both': 'To: submission email · CC: ISO rep',
  'Rep Email': 'To: ISO rep email',
  'Multi Emails': 'To: submission + additional emails · CC: ISO rep',
  'Portal': 'No auto email — submit via the funder portal',
};

export interface FunderSettingsData {
  id: number;
  routing_mode: string | null;
  submission_email: string | null;
  additional_submission_emails: string | null;
  submission_cc_emails: string | null;
  rep1_email: string | null;
  rep1_first_name: string | null;
  rep1_last_name: string | null;
  rep1_phone: string | null;
  portal_link: string | null;
  prohibited_states: string | null;
  prohibited_industries: string | null;
  drive_folder_link: string | null;
  website: string | null;
  note: string | null;
  state_overrides: Record<string, { to?: string[]; cc?: string[] }> | null;
  /** REG-902: deals go out as Region (from deals@, CC deals@ + levi@). */
  is_region: boolean;
  region_submission_email: string | null;
}

type Draft = Record<Exclude<keyof FunderSettingsData, 'id' | 'state_overrides' | 'is_region'>, string>;

const DRAFT_KEYS: (keyof Draft)[] = [
  'routing_mode', 'submission_email', 'region_submission_email', 'additional_submission_emails', 'submission_cc_emails',
  'rep1_email', 'rep1_first_name', 'rep1_last_name', 'rep1_phone', 'portal_link',
  'prohibited_states', 'prohibited_industries', 'drive_folder_link', 'website', 'note',
];

function toDraft(funder: FunderSettingsData): Draft {
  const draft = {} as Draft;
  for (const key of DRAFT_KEYS) draft[key] = (funder[key] ?? '').trim();
  return draft;
}

function Field({ label, value, onChange, placeholder, wide }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; wide?: boolean;
}) {
  return (
    <label className={`funder-field ${wide ? 'wide' : ''}`}>
      <span>{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || '—'} />
    </label>
  );
}

export default function FunderSettings({ funder, onSaved }: { funder: FunderSettingsData; onSaved: () => void }) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(funder));
  const [isRegion, setIsRegion] = useState(funder.is_region);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  // Re-seed when switching funders or after a refetch lands new values.
  useEffect(() => { setDraft(toDraft(funder)); setIsRegion(funder.is_region); setError(''); setSaved(false); }, [funder]);

  const dirty = useMemo(() => {
    const base = toDraft(funder);
    return isRegion !== funder.is_region || DRAFT_KEYS.some((key) => draft[key] !== base[key]);
  }, [draft, funder, isRegion]);

  const set = (key: keyof Draft) => (value: string) => { setDraft((d) => ({ ...d, [key]: value })); setSaved(false); };
  const mode = draft.routing_mode || 'Submission Email';

  const overrides = funder.state_overrides;
  const overrideNote = overrides && Object.keys(overrides).length
    ? Object.entries(overrides).map(([st, r]) => `${st} → ${(r.to ?? []).join(', ') || '—'}`).join(' · ')
    : null;

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true); setError('');
    const payload: Record<string, string | boolean | null> = { is_region: isRegion };
    for (const key of DRAFT_KEYS) payload[key] = draft[key].trim() || null;
    // .select('id') is the RLS probe: funder UPDATE requires edit_deals
    // (Admin, Manager, Exec, Dev); a filtered zero-row result is not a save.
    const { data: updated, error: updateErr } = await (supabaseClient as any)
      .from('funder').update(payload).eq('id', funder.id).select('id');
    setSaving(false);
    if (updateErr) { setError(updateErr.message); return; }
    if (!updated?.length) { setError('You do not have permission to edit funder settings.'); return; }
    setSaved(true);
    onSaved();
  };

  return (
    <section className="rep-data-card funder-settings">
      <div className="rep-card-title">
        <b>Funder Settings</b>
        <span>routing, restrictions &amp; contact — applies to future submissions</span>
        <em className="funder-settings-actions">
          {error && <i className="funder-settings-error" title={error}>{error}</i>}
          {saved && !dirty && <i className="funder-settings-saved">Saved</i>}
          <button className="funder-save" disabled={!dirty || saving} onClick={save}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </em>
      </div>
      <div className="funder-settings-grid">
        <div className="funder-settings-group">
          <h4>Submission Routing</h4>
          <label className="funder-field">
            <span>Sends As</span>
            <span className="funder-settings-region">
              <input
                type="checkbox"
                checked={isRegion}
                onChange={(e) => { setIsRegion(e.target.checked); setSaved(false); }}
                aria-label="Region funder"
              />
              Region funder
            </span>
            <small>
              {isRegion
                ? 'Sent as Region: from deals@regioncapitalgroup.com, CC deals@ and levi@, BUSINESS - REGION application. No Skyline address or rep CC.'
                : 'Sent as Skyline from Admin@skylinecaps.com'}
            </small>
          </label>
          <label className="funder-field">
            <span>Routing Mode</span>
            <select value={mode} onChange={(e) => set('routing_mode')(e.target.value)}>
              {ROUTING_MODES.map((m) => <option key={m}>{m}</option>)}
            </select>
            <small>{MODE_HELP[mode]}</small>
          </label>
          <Field label="Submission Email" value={draft.submission_email} onChange={set('submission_email')} placeholder="subs@funder.com" />
          {isRegion && (
            <Field label="Region Submission Email" value={draft.region_submission_email} onChange={set('region_submission_email')} placeholder="optional: replaces Submission Email for Region deals" />
          )}
          {mode === 'Multi Emails' && (
            <Field label="Additional Emails (To)" value={draft.additional_submission_emails} onChange={set('additional_submission_emails')} placeholder="second@funder.com, third@funder.com" />
          )}
          <Field label="CC Emails" value={draft.submission_cc_emails} onChange={set('submission_cc_emails')} placeholder="always CC'd on submissions" />
          <Field label="Portal Link" value={draft.portal_link} onChange={set('portal_link')} placeholder="https://…" />
          {overrideNote && (
            <p className="funder-override-note" title="Per-state recipient overrides (state_overrides on the funder record)">
              State overrides: {overrideNote}
            </p>
          )}
        </div>
        <div className="funder-settings-group">
          <h4>Restrictions</h4>
          <Field label="Prohibited States" value={draft.prohibited_states} onChange={set('prohibited_states')} placeholder="TX, CA, PR" />
          <Field label="Prohibited Industries" value={draft.prohibited_industries} onChange={set('prohibited_industries')} placeholder="Construction, Trucking" />
          <h4>Links</h4>
          <Field label="Drive Folder" value={draft.drive_folder_link} onChange={set('drive_folder_link')} placeholder="https://drive.google.com/…" />
          <Field label="Website" value={draft.website} onChange={set('website')} />
        </div>
        <div className="funder-settings-group">
          <h4>ISO Rep Contact</h4>
          <div className="funder-field-row">
            <Field label="First Name" value={draft.rep1_first_name} onChange={set('rep1_first_name')} />
            <Field label="Last Name" value={draft.rep1_last_name} onChange={set('rep1_last_name')} />
          </div>
          <Field label="Rep Email" value={draft.rep1_email} onChange={set('rep1_email')} />
          <Field label="Rep Phone" value={draft.rep1_phone} onChange={set('rep1_phone')} />
          <h4>Notes</h4>
          <label className="funder-field wide">
            <span>Note</span>
            <textarea rows={3} value={draft.note} onChange={(e) => set('note')(e.target.value)} placeholder="—" />
          </label>
        </div>
      </div>
    </section>
  );
}
